const ValidationSchemaFactory = require('./ValidationSchemaFactory');
const ValidationError = require('./errors/ValidationError');
const TransferPixService = require('@transfeeradev/api-services/TransferPixService');
const { errors } = require('@transfeeradev/api-enums');
const defaultSchema = require('./schemas/defaultSchema');

module.exports = class SchemaValidation {
  constructor(bankAccount, bankCode, ignoreErrorsWithSuggestions) {
    this._bankAccount = bankAccount;
    this._bankCode = bankCode;
    this._ignoreErrorsWithSuggestions = ignoreErrorsWithSuggestions;
    this._schema = new ValidationSchemaFactory(this._bankCode, this._bankAccount).build();
  }

  getSchema() {
    return this._schema;
  }

  validate() {
    let errors = [];

    try {
      this.validateBank();
    } catch (e) {
      errors = this._onValidationError(e, errors);
    }

    try {
      this.validateAgency();
    } catch (e) {
      errors = this._onValidationError(e, errors);
    }

    try {
      this.validateAgencyDigit();
    } catch (e) {
      errors = this._onValidationError(e, errors);
    }

    try {
      this.validateAccount();
    } catch (e) {
      errors = this._onValidationError(e, errors);
    }

    try {
      this.validateAccountDigit();
    } catch (e) {
      errors = this._onValidationError(e, errors);
    }

    try {
      this.validateAccountType();
    } catch (e) {
      errors = this._onValidationError(e, errors);
    }

    return errors;
  }

  validateBankCode() {
    return this._validateField(
      this._schema.bankCode,
      this._bankAccount.bank_code,
      'bank_code',
      this._schema.bankCode.moreOpenPattern
    );
  }

  validateBankISPB() {
    return this._validateField(
      this._schema.bankISPB,
      this._bankAccount.bank_ispb,
      'bank_ispb',
      this._schema.bankISPB.moreOpenPattern
    );
  }

  validateBank() {
    this.validateBankCode();
    this.validateBankISPB();

    if (!this._bankAccount.bank_code && !this._bankAccount.bank_ispb) {
      const error = errors.ptBr.dba.EMPTY_BANK_CODE_AND_ISPB;
      throw new ValidationError(error.message, error.field, null, error.code);
    } else if (this._bankAccount.bank_code && this._bankAccount.bank_ispb) {
      const error = errors.ptBr.dba.PROVIDED_BANK_CODE_AND_ISPB;
      throw new ValidationError(error.message, error.field, null, error.code);
    }
  }

  validateAgency() {
    return this._validateField(
      this._schema.agency,
      this._bankAccount.agency,
      'agency',
      this._schema.agency.moreOpenPattern
    );
  }

  validateAgencyDigit() {
    return this._validateField(
      this._schema.agency.digit,
      this._bankAccount.agency_digit,
      'agency_digit',
      this._schema.agency.digit.moreOpenPattern
    );
  }

  validateAccount() {
    let propSchema = this._schema.account;
    if (TransferPixService.isPix(this._bankAccount)) {
      propSchema = {
        ...propSchema,
        maxLength: defaultSchema.maxLength
      };
    }

    return this._validateField(propSchema, this._bankAccount.account, 'account', this._schema.account.moreOpenPattern);
  }

  validateAccountDigit() {
    return this._validateField(
      this._schema.account.digit,
      this._bankAccount.account_digit,
      'account_digit',
      this._schema.account.digit.moreOpenPattern
    );
  }

  validateAccountType() {
    if (this._schema.accountType && this._schema.accountType.required && !this._hasAccountType()) {
      let suggestion = null;
      let valuePropName = 'account_type';

      if (this._schema.accountType.suggestion) {
        suggestion = {
          [valuePropName]: this._schema.accountType.suggestion
        };
      }

      throw new ValidationError(
        this._schema.accountType.errorMessage,
        valuePropName,
        suggestion,
        this._schema.accountType.errorCode,
        this._schema.accountType.errorData
      );
    }
  }

  _validateField(validation, value, valuePropName, moreOpenPattern) {
    let isValid = true;
    if (validation.required || value || value === 0) {
      isValid = this._isFieldValid(validation, value);
    }

    if (!isValid) {
      if (
        validation.suggestion &&
        (!moreOpenPattern || moreOpenPattern.test(value)) &&
        this._ignoreErrorsWithSuggestions
      ) {
        return true;
      }
      if (validation.suggestion) {
        throw new ValidationError(
          validation.errorMessage,
          valuePropName,
          {
            [valuePropName]: validation.suggestion
          },
          validation.errorCode,
          validation.errorData
        );
      } else {
        throw new ValidationError(
          validation.errorMessage,
          valuePropName,
          null,
          validation.errorCode,
          validation.errorData
        );
      }
    }
  }

  _isFieldValid(validation, value) {
    const isLengthValid = Boolean(
      (!validation.maxLength && !validation.exactLength) ||
        (validation.exactLength && this._isExactLength(validation, value)) ||
        (!validation.exactLength && validation.maxLength && this._isLessThanOrEqualLength(validation, value))
    );
    return Boolean(
      (value || value === 0) && isLengthValid && (!validation.pattern || validation.pattern.test(String(value)))
    );
  }

  _isExactLength(validation, value) {
    return Boolean(String(value).length === validation.exactLength);
  }

  _isLessThanOrEqualLength(validation, value) {
    return Boolean(this._removeUnnecessaryLeadingZeros(String(value)).length <= validation.maxLength);
  }

  _hasAccountType() {
    if (Array.isArray(this._schema.accountType.allowedTypes)) {
      return Boolean(this._schema.accountType.allowedTypes.includes(this._bankAccount.account_type));
    }
    return true;
  }

  _removeUnnecessaryLeadingZeros(value) {
    if (value) {
      return value.replace(/^0+/, '');
    }
    return value;
  }

  _onValidationError(error, errors) {
    if (error instanceof ValidationError) {
      errors.push(error);
    } else {
      throw error;
    }
    return errors;
  }
};
